<template>
  <div>
    <!-- Loading skeletons -->
    <div v-if="loading" class="skel-grid">
      <div v-for="i in 4" :key="`skel-${i}`" class="skel-card"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="reviews.length === 0" class="empty-state">
      <v-icon size="48" color="#c5cbd3" class="mb-4">{{ emptyIcon }}</v-icon>
      <h3 class="empty-title">{{ emptyTitle }}</h3>
      <p v-if="emptyHint" class="empty-desc">{{ emptyHint }}</p>
    </div>

    <!-- Grouped by shop -->
    <template v-else>
      <div v-for="group in groupedReviews" :key="group.shop" class="shop-group">
        <button
          type="button"
          class="shop-header"
          :aria-expanded="!isCollapsed(group.shop)"
          @click="toggleGroup(group.shop)"
        >
          <div class="shop-icon"><v-icon size="16" color="#5B5BD6">mdi-store-outline</v-icon></div>
          <span class="shop-name">{{ group.shop }}</span>
          <span class="shop-count">{{ group.reviews.length }}</span>
          <v-icon
            size="16"
            color="#888"
            class="shop-chev"
            :class="{ 'shop-chev-collapsed': isCollapsed(group.shop) }"
          >mdi-chevron-down</v-icon>
        </button>
        <div v-if="!isCollapsed(group.shop)" class="cards-grid">
          <ReviewCard
            v-for="review in group.reviews"
            :key="review.id"
            :review="review"
            :current-user="currentUser"
            :context="context"
            :accepting-id="acceptingId"
            :resubmitting-id="resubmittingId"
            :archiving-id="archivingId"
            @accept="$emit('accept', $event)"
            @reject="$emit('reject', $event)"
            @resubmit="$emit('resubmit', $event)"
            @archive="$emit('archive', $event)"
            @unarchive="$emit('unarchive', $event)"
            @open-screenshot="$emit('open-screenshot', $event)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ReviewCard from './ReviewCard.vue';

export default {
  name: 'ReviewsList',
  components: { ReviewCard },
  props: {
    reviews: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    currentUser: { type: Object, default: () => ({}) },
    context: { type: String, default: 'mine' },
    emptyTitle: { type: String, default: 'Keine Reviews vorhanden' },
    emptyHint: { type: String, default: '' },
    emptyIcon: { type: String, default: 'mdi-clipboard-list-outline' },
    acceptingId: { type: [Number, String, null], default: null },
    resubmittingId: { type: [Number, String, null], default: null },
    archivingId: { type: [Number, String, null], default: null },
  },
  emits: ['accept', 'reject', 'resubmit', 'archive', 'unarchive', 'open-screenshot'],

  data() {
    return {
      collapsedShops: this.loadCollapsed(),
    };
  },

  computed: {
    groupedReviews() {
      const map = {};
      for (const r of this.reviews) {
        const shop = r.shopDomain || 'Kein Shop angegeben';
        if (!map[shop]) map[shop] = [];
        map[shop].push(r);
      }
      return Object.keys(map).sort().map((shop) => ({ shop, reviews: map[shop] }));
    },
  },

  methods: {
    storageKey() { return `reviews.collapsedShops.${this.context}`; },
    loadCollapsed() {
      try {
        const raw = localStorage.getItem(`reviews.collapsedShops.${this.context}`);
        return raw ? new Set(JSON.parse(raw)) : new Set();
      } catch {
        return new Set();
      }
    },
    persistCollapsed() {
      try {
        localStorage.setItem(this.storageKey(), JSON.stringify([...this.collapsedShops]));
      } catch (_e) { /* ignore quota errors */ }
    },
    isCollapsed(shop) { return this.collapsedShops.has(shop); },
    toggleGroup(shop) {
      const next = new Set(this.collapsedShops);
      if (next.has(shop)) next.delete(shop);
      else next.add(shop);
      this.collapsedShops = next;
      this.persistCollapsed();
    },
  },
};
</script>

<style scoped>
.shop-group { margin-bottom: 28px; }
.shop-group:last-child { margin-bottom: 0; }

.shop-header {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 10px 14px; margin-bottom: 12px;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  cursor: pointer; text-align: left; font-family: inherit;
  transition: border-color 0.15s, background 0.15s;
}
.shop-header:hover { border-color: #c5cbd3; background: #fafbfc; }
.shop-chev { margin-left: 4px; transition: transform 0.2s ease; }
.shop-chev-collapsed { transform: rotate(-90deg); }
.shop-icon {
  width: 28px; height: 28px; border-radius: 8px;
  background: rgba(91,91,214,0.08);
  display: flex; align-items: center; justify-content: center;
}
.shop-name { flex: 1; font-size: 14px; font-weight: 700; color: #0a1628; }
.shop-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 22px; padding: 0 7px;
  background: #f0f2f5; color: #666;
  border-radius: 11px; font-size: 11px; font-weight: 700;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 14px;
}

/* Empty */
.empty-state {
  background: white; border: 1px solid #eceef2; border-radius: 16px;
  padding: 64px 32px; text-align: center;
}
.empty-title { font-size: 16px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.empty-desc { font-size: 13px; color: #888; max-width: 480px; margin: 0 auto; }

/* Skeleton */
.skel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 14px;
}
.skel-card {
  height: 320px; border-radius: 14px;
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite;
}
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
</style>
